/**
 * Expenses — Risk & Finance → Expenses (Iter 134)
 *
 * Combined dashboard + list + create modal. Kept in a single page to
 * match the pattern used elsewhere in the finance module (Invoices,
 * Transactions).
 */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import {
  Plus, Wallet, Warning, CircleNotch, CheckCircle, X, Truck,
  Receipt, Funnel, PencilSimple, Trash,
} from "@phosphor-icons/react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart,
  Line, CartesianGrid,
} from "recharts";

const EXPENSE_TYPES = [
  "Fuel", "Maintenance", "Office", "Travel", "Salary Advance",
  "Stationery", "Utilities", "Marketing", "Miscellaneous",
];
const PAYMENT_METHODS = ["Cash", "UPI", "Bank Transfer", "Card"];
const APPROVAL_STATUSES = ["Pending", "Approved", "Rejected"];

const fmtInr = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 })
    .format(Number(n || 0));

// Iter 134 fix — proper IST date helpers using Intl.DateTimeFormat.
// The previous implementation double-adjusted (getTimezoneOffset already
// zeroed out on Asia/Kolkata browsers). This formatter always produces
// the wall-clock date in Kolkata, regardless of the user's timezone.
const _KOLKATA_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
});
const _istIso = (d = new Date()) => _KOLKATA_FMT.format(d);  // "YYYY-MM-DD"
const today = () => _istIso();
const daysAgoIso = (n) => _istIso(new Date(Date.now() - n * 86400_000));
const startOfWeek = () => {
  // Compute Monday-of-week for the *current IST day*.
  const iso = today();               // YYYY-MM-DD in IST
  const d = new Date(iso + "T00:00:00Z");  // treat that IST date as UTC midnight
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
};
const startOfMonth = () => {
  const iso = today();
  return iso.slice(0, 8) + "01";
};

const DATE_PRESETS = [
  { key: "today",     label: "Today",       from: () => today(),         to: () => today() },
  { key: "yesterday", label: "Yesterday",   from: () => daysAgoIso(1),   to: () => daysAgoIso(1) },
  { key: "week",      label: "This Week",   from: () => startOfWeek(),   to: () => today() },
  { key: "month",     label: "This Month",  from: () => startOfMonth(),  to: () => today() },
];

export default function Expenses() {
  const { user } = useAuth();
  // Iter 134 fix — role-gated action buttons. Backend uses the same
  // partitioning: approve/reject in _APPROVE, delete in (admin, superadmin).
  const canApprove = ["superadmin", "admin", "manager"].includes(user?.role);
  const canDelete  = ["superadmin", "admin"].includes(user?.role);
  const [tab, setTab] = useState("dashboard"); // dashboard | list
  const [dashboard, setDashboard] = useState(null);
  const [loadingDash, setLoadingDash] = useState(false);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [filters, setFilters] = useState({
    preset: "month",
    date_from: startOfMonth(),
    date_to: today(),
    department: "",
    expense_type: "",
    staff_id: "",
    vehicle_id: "",
    payment_method: "",
    approval_status: "",
  });
  const [staff, setStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadDashboard = () => {
    setLoadingDash(true);
    api.get("/expenses-dashboard")
      .then(r => setDashboard(r.data))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoadingDash(false));
  };

  const loadRows = () => {
    setLoadingRows(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (k === "preset") return;
      if (v) params[k] = v;
    });
    api.get("/expenses", { params })
      .then(r => setRows(r.data || []))
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoadingRows(false));
  };

  useEffect(() => {
    loadDashboard();
    api.get("/staff?status=active").then(r => setStaff(r.data || [])).catch(() => {});
    api.get("/vehicles").then(r => setVehicles(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "list") loadRows();
    // eslint-disable-next-line
  }, [tab, filters]);

  const applyPreset = (key) => {
    const p = DATE_PRESETS.find(x => x.key === key);
    if (!p) return;
    setFilters(f => ({ ...f, preset: key, date_from: p.from(), date_to: p.to() }));
  };

  const onSaved = () => {
    setModalOpen(false); setEditing(null);
    loadDashboard();
    if (tab === "list") loadRows();
  };

  const approve = async (id, status) => {
    try {
      await api.post(`/expenses/${id}/approve`, { status });
      toast.success(`${status}`);
      loadRows(); loadDashboard();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Deleted");
      loadRows(); loadDashboard();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AppShell title="Expenses" subtitle="Module 8 · Risk & Finance"
      actions={
        <button onClick={() => { setEditing(null); setModalOpen(true); }}
                data-testid="btn-new-expense"
                className="h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium inline-flex items-center gap-2">
          <Plus size={14} weight="bold"/>New expense
        </button>
      }>
      <div className="flex gap-2 mb-4">
        {["dashboard", "list"].map(t => (
          <button key={t} onClick={() => setTab(t)}
                  data-testid={`exp-tab-${t}`}
                  className={`h-9 px-4 text-xs uppercase tracking-[0.16em] font-semibold rounded-md ${
                    tab === t ? "bg-[#1F3A57] text-white"
                              : "bg-white border border-[#E5E2D9] text-[#5C5A55]"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "dashboard" ? (
        <DashboardView dashboard={dashboard} loading={loadingDash}/>
      ) : (
        <ListView rows={rows} loading={loadingRows}
                   filters={filters} setFilters={setFilters}
                   applyPreset={applyPreset}
                   staff={staff} vehicles={vehicles}
                   canApprove={canApprove} canDelete={canDelete}
                   onEdit={(r) => { setEditing(r); setModalOpen(true); }}
                   onApprove={approve} onDelete={del}/>
      )}

      {modalOpen && (
        <ExpenseModal expense={editing} onClose={() => { setModalOpen(false); setEditing(null); }}
                       staff={staff} vehicles={vehicles} onSaved={onSaved}/>
      )}
    </AppShell>
  );
}

// -------------------------- Dashboard --------------------------
function DashboardView({ dashboard, loading }) {
  if (loading || !dashboard) {
    return <div className="flex items-center justify-center h-64 text-[#8A8780] text-sm">
      <CircleNotch size={16} className="animate-spin mr-2"/>Loading…
    </div>;
  }
  const tiles = [
    { label: "Today", value: dashboard.today, tone: "green", testid: "kpi-today" },
    { label: "This Week", value: dashboard.week, tone: "blue", testid: "kpi-week" },
    { label: "This Month", value: dashboard.month, tone: "amber", testid: "kpi-month" },
    { label: "Pending Approval", value: dashboard.pending_approval, tone: "red", testid: "kpi-pending" },
  ];
  const toneCls = {
    green: "bg-[#D4E0D1] text-[#2D5A27]",
    blue: "bg-[#DDE7F1] text-[#1F3A57]",
    amber: "bg-[#F9E4B7] text-[#7A5A11]",
    red: "bg-[#F4C7B9] text-[#7A2E18]",
  };
  return (
    <div className="space-y-4" data-testid="exp-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiles.map(t => (
          <div key={t.label} data-testid={t.testid}
               className="bg-white border border-[#E5E2D9] rounded-md p-4">
            <div className={`inline-block text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded ${toneCls[t.tone]} mb-2`}>
              {t.label}
            </div>
            <div className="text-2xl font-bold text-[#1A1A1A]">₹{fmtInr(t.value)}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-4">
        <Panel title="Monthly Trend (6 mo)" className="col-span-12 lg:col-span-8" testid="exp-trend">
          {dashboard.monthly_trend?.length ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={dashboard.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDEBE3"/>
                <XAxis dataKey="month" stroke="#8A8780" fontSize={11}/>
                <YAxis stroke="#8A8780" fontSize={11}
                       tickFormatter={v => `₹${(v/1000).toFixed(0)}k`}/>
                <Tooltip formatter={v => `₹${fmtInr(v)}`}/>
                <Line type="monotone" dataKey="amount" stroke="#2D5A27" strokeWidth={2} dot/>
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty label="No trend data yet"/>}
        </Panel>

        <Panel title="Top 10 highest" className="col-span-12 lg:col-span-4" testid="exp-top10">
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {(dashboard.top10 || []).length === 0 && <Empty label="—"/>}
            {(dashboard.top10 || []).map((e, i) => (
              <div key={e.id} className="flex items-center gap-2 text-xs border-b border-[#F5F2EA] pb-1">
                <span className="w-5 h-5 rounded-full bg-[#EFF3EE] text-[#2D5A27] text-[10px] font-bold flex items-center justify-center">{i+1}</span>
                <span className="flex-1 truncate">
                  {e.expense_type} <span className="text-[#8A8780]">· {e.expense_date}</span>
                </span>
                <span className="font-semibold">₹{fmtInr(e.amount)}</span>
              </div>
            ))}
          </div>
        </Panel>

        <BarPanel title="By Department" data={dashboard.by_department} testid="exp-by-dept"/>
        <BarPanel title="By Expense Type" data={dashboard.by_type} testid="exp-by-type"/>
        <BarPanel title="By Vehicle" data={dashboard.by_vehicle} testid="exp-by-vehicle"/>
        <BarPanel title="By Staff" data={dashboard.by_staff} testid="exp-by-staff"/>
      </div>
    </div>
  );
}

const Panel = ({ title, children, className = "", testid }) => (
  <div className={`bg-white border border-[#E5E2D9] rounded-md p-4 ${className}`}
       data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] mb-2">{title}</div>
    {children}
  </div>
);
const Empty = ({ label }) => (
  <div className="text-[11px] text-[#8A8780] italic py-6 text-center">{label}</div>
);

const BarPanel = ({ title, data, testid }) => (
  <Panel title={title} className="col-span-12 md:col-span-6 lg:col-span-6" testid={testid}>
    {data?.length ? (
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} layout="vertical">
          <XAxis type="number" hide/>
          <YAxis type="category" dataKey="key" stroke="#5C5A55" fontSize={11} width={120}/>
          <Tooltip formatter={v => `₹${fmtInr(v)}`}/>
          <Bar dataKey="amount" fill="#2D5A27" radius={[0, 4, 4, 0]}/>
        </BarChart>
      </ResponsiveContainer>
    ) : <Empty label="No data"/>}
  </Panel>
);

// -------------------------- List --------------------------
function ListView({ rows, loading, filters, setFilters, applyPreset,
                    staff, vehicles, canApprove, canDelete, onEdit,
                    onApprove, onDelete }) {
  return (
    <div className="space-y-3">
      <div className="bg-white border border-[#E5E2D9] rounded-md p-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Funnel size={12} className="text-[#5C5A55]"/>
          {DATE_PRESETS.map(p => (
            <button key={p.key} onClick={() => applyPreset(p.key)}
                    data-testid={`preset-${p.key}`}
                    className={`h-8 px-3 rounded-md text-[11px] border ${
                      filters.preset === p.key
                        ? "bg-[#1F3A57] text-white border-[#1F3A57]"
                        : "bg-white border-[#E5E2D9] text-[#5C5A55]"}`}>
              {p.label}
            </button>
          ))}
          <input type="date" value={filters.date_from}
                 onChange={e => setFilters(f => ({ ...f, preset: "custom", date_from: e.target.value }))}
                 data-testid="filter-date-from"
                 className="h-8 px-2 border border-[#E5E2D9] rounded-md text-xs"/>
          <span className="text-[10px] text-[#8A8780]">→</span>
          <input type="date" value={filters.date_to}
                 onChange={e => setFilters(f => ({ ...f, preset: "custom", date_to: e.target.value }))}
                 data-testid="filter-date-to"
                 className="h-8 px-2 border border-[#E5E2D9] rounded-md text-xs"/>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select label="Type" value={filters.expense_type} testid="filter-type"
                  onChange={v => setFilters(f => ({ ...f, expense_type: v }))}
                  options={EXPENSE_TYPES}/>
          <Input label="Department" value={filters.department} testid="filter-dept"
                 onChange={v => setFilters(f => ({ ...f, department: v }))}/>
          <Select label="Staff" value={filters.staff_id} testid="filter-staff"
                  onChange={v => setFilters(f => ({ ...f, staff_id: v }))}
                  options={staff.map(s => ({ v: s.id, l: s.name }))}/>
          <Select label="Vehicle" value={filters.vehicle_id} testid="filter-vehicle"
                  onChange={v => setFilters(f => ({ ...f, vehicle_id: v }))}
                  options={vehicles.map(v => ({ v: v.id, l: v.vehicle_no || v.id }))}/>
          <Select label="Payment" value={filters.payment_method} testid="filter-payment"
                  onChange={v => setFilters(f => ({ ...f, payment_method: v }))}
                  options={PAYMENT_METHODS}/>
          <Select label="Approval" value={filters.approval_status} testid="filter-approval"
                  onChange={v => setFilters(f => ({ ...f, approval_status: v }))}
                  options={APPROVAL_STATUSES}/>
        </div>
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#FAF8F2] text-[10px] uppercase tracking-[0.14em] text-[#5C5A55]">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Dept</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-left px-3 py-2">Staff / Vehicle</th>
              <th className="text-left px-3 py-2">Payment</th>
              <th className="text-right px-3 py-2">Amount</th>
              <th className="text-left px-3 py-2">Approval</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="exp-list-body">
            {loading && <tr><td colSpan={9} className="p-6 text-center text-[#8A8780]"><CircleNotch size={14} className="animate-spin inline mr-2"/>Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-[#8A8780]">No expenses in this range.</td></tr>
            )}
            {!loading && rows.map(r => (
              <tr key={r.id} data-testid={`exp-row-${r.id}`}
                  className={`border-t border-[#F5F2EA] ${r.linked_source ? "bg-[#F7F9FB]" : ""}`}>
                <td className="px-3 py-2 font-mono text-[10px]">{r.expense_date}</td>
                <td className="px-3 py-2">
                  {r.expense_type}
                  {r.linked_source && (
                    <span title={`Linked from ${r.linked_source}`}
                          className="ml-1 text-[9px] px-1 rounded bg-[#DDE7F1] text-[#1F3A57]">
                      <Truck size={9} className="inline"/> linked
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{r.department || "—"}</td>
                <td className="px-3 py-2 max-w-[180px] truncate" title={r.description}>{r.description || "—"}</td>
                <td className="px-3 py-2 text-[10px] text-[#5C5A55]">
                  {r.staff_name && <div>{r.staff_name}</div>}
                  {r.vehicle_no && <div className="font-mono">{r.vehicle_no}</div>}
                </td>
                <td className="px-3 py-2">{r.payment_method}</td>
                <td className="px-3 py-2 text-right font-semibold">₹{fmtInr(r.amount)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                    r.approval_status === "Approved" ? "bg-[#D4E0D1] text-[#2D5A27]"
                    : r.approval_status === "Rejected" ? "bg-[#F4C7B9] text-[#7A2E18]"
                    : "bg-[#F9E4B7] text-[#7A5A11]"}`}>
                    {r.approval_status}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap flex gap-1">
                  {r.approval_status === "Pending" && canApprove && (
                    <>
                      <button onClick={() => onApprove(r.id, "Approved")}
                              data-testid={`exp-approve-${r.id}`}
                              title="Approve"
                              className="h-7 px-2 rounded bg-[#2D5A27] text-white text-[10px]">
                        <CheckCircle size={10} weight="fill"/>
                      </button>
                      <button onClick={() => onApprove(r.id, "Rejected")}
                              data-testid={`exp-reject-${r.id}`}
                              title="Reject"
                              className="h-7 px-2 rounded bg-[#7A2E18] text-white text-[10px]">
                        <X size={10} weight="bold"/>
                      </button>
                    </>
                  )}
                  {!r.linked_source && (
                    <button onClick={() => onEdit(r)}
                            data-testid={`exp-edit-${r.id}`}
                            className="h-7 px-2 rounded border border-[#E5E2D9] text-[10px] text-[#5C5A55]">
                      <PencilSimple size={10}/>
                    </button>
                  )}
                  {!r.linked_source && canDelete && (
                    <button onClick={() => onDelete(r.id)}
                            data-testid={`exp-del-${r.id}`}
                            className="h-7 px-2 rounded border border-[#F4C7B9] text-[#7A2E18] text-[10px]">
                      <Trash size={10}/>
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Select = ({ label, value, onChange, options, testid }) => (
  <select value={value} onChange={e => onChange(e.target.value)}
          data-testid={testid}
          className="h-8 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white">
    <option value="">{label} · all</option>
    {options.map(o => {
      const isObj = typeof o === "object";
      const v = isObj ? o.v : o;
      const l = isObj ? o.l : o;
      return <option key={v} value={v}>{l}</option>;
    })}
  </select>
);

const Input = ({ label, value, onChange, testid }) => (
  <input placeholder={label} value={value} onChange={e => onChange(e.target.value)}
         data-testid={testid}
         className="h-8 px-2 border border-[#E5E2D9] rounded-md text-xs w-32"/>
);

// -------------------------- Create/Edit Modal --------------------------
function ExpenseModal({ expense, onClose, staff, vehicles, onSaved }) {
  const isNew = !expense?.id;
  const [form, setForm] = useState(() => ({
    expense_date: expense?.expense_date || today(),
    expense_type: expense?.expense_type || "Office",
    department: expense?.department || "",
    staff_id: expense?.staff_id || "",
    vehicle_id: expense?.vehicle_id || "",
    description: expense?.description || "",
    amount: expense?.amount || "",
    payment_method: expense?.payment_method || "Cash",
    vendor: expense?.vendor || "",
    remarks: expense?.remarks || "",
  }));
  const [saving, setSaving] = useState(false);

  const F = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async (ev) => {
    ev.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Amount must be greater than zero"); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      Object.keys(payload).forEach(k => { if (payload[k] === "") payload[k] = null; });
      if (isNew) {
        await api.post("/expenses", payload);
        toast.success("Expense recorded");
      } else {
        await api.put(`/expenses/${expense.id}`, payload);
        toast.success("Expense updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="exp-modal">
      <div className="bg-white rounded-md max-w-2xl w-full">
        <form onSubmit={save}>
          <div className="flex items-center justify-between p-4 border-b border-[#F0EEE6]">
            <div className="flex items-center gap-2">
              <Receipt size={18} weight="duotone" className="text-[#2D5A27]"/>
              <h2 className="text-sm font-semibold uppercase tracking-[0.14em]">
                {isNew ? "New expense" : "Edit expense"}
              </h2>
            </div>
            <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[#F5F2EA]" data-testid="exp-modal-close">
              <X size={16}/>
            </button>
          </div>
          <div className="p-4 grid grid-cols-2 gap-3">
            <Fld label="Date">
              <input type="date" value={form.expense_date} onChange={F("expense_date")}
                     data-testid="exp-date" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </Fld>
            <Fld label="Type">
              <select value={form.expense_type} onChange={F("expense_type")}
                      data-testid="exp-type" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Fld>
            <Fld label="Department">
              <input value={form.department} onChange={F("department")} placeholder="e.g., Operations"
                     data-testid="exp-department" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </Fld>
            <Fld label="Payment Method">
              <select value={form.payment_method} onChange={F("payment_method")}
                      data-testid="exp-payment" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                {PAYMENT_METHODS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Fld>
            <Fld label="Staff (optional)">
              <select value={form.staff_id} onChange={F("staff_id")}
                      data-testid="exp-staff" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                <option value="">—</option>
                {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </Fld>
            <Fld label="Vehicle (optional)">
              <select value={form.vehicle_id} onChange={F("vehicle_id")}
                      data-testid="exp-vehicle" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
                <option value="">—</option>
                {vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_no || v.id}</option>)}
              </select>
            </Fld>
            <Fld label="Amount">
              <input type="number" min={0.01} step={0.01} required
                     value={form.amount} onChange={F("amount")}
                     data-testid="exp-amount" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </Fld>
            <Fld label="Vendor / Supplier (optional)">
              <input value={form.vendor} onChange={F("vendor")}
                     data-testid="exp-vendor" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </Fld>
            <Fld label="Description" className="col-span-2">
              <input value={form.description} onChange={F("description")}
                     placeholder="What is this expense for?"
                     data-testid="exp-description" className="w-full h-9 px-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </Fld>
            <Fld label="Remarks" className="col-span-2">
              <textarea value={form.remarks} onChange={F("remarks")} rows={2}
                        data-testid="exp-remarks" className="w-full p-2 border border-[#E5E2D9] rounded-md text-sm"/>
            </Fld>
          </div>
          <div className="p-4 border-t border-[#F0EEE6] flex justify-end gap-2">
            <button type="button" onClick={onClose}
                    className="h-10 px-4 rounded-md border border-[#E5E2D9] text-sm">Cancel</button>
            <button type="submit" disabled={saving}
                    data-testid="exp-save"
                    className="h-10 px-4 rounded-md bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium inline-flex items-center gap-2">
              {saving ? <CircleNotch size={12} className="animate-spin"/> : <CheckCircle size={12} weight="fill"/>}
              {saving ? "Saving…" : isNew ? "Save expense" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const Fld = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] block mb-1">{label}</span>
    {children}
  </label>
);

// eslint-disable-next-line no-unused-vars
const _wallet = Wallet; // keep import used for legends elsewhere later
// eslint-disable-next-line no-unused-vars
const _warning = Warning;
